<template>
    <div class="dashboard">
        <div class="row gy-3 mb-4">
            <div class="col-md-12">
                <h5 class="mb-2">Hi, {{ activeUser.first_name }}</h5>
                <p class="mb-0">
                    Welcome to the Akwa Ibom State Payroll Employee Dashboard.
                </p>
            </div>
        </div>
        <div class="row mb-4 g-4">
            <div class="col-md-4">
                <div class="card h-100 mb-0 card-animate overflow-hidden" style="min-height: 100px;">
                    <common-skeleton-loader position="absolute" v-if="isLoading" />
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1 overflow-hidden">
                                <p class="text-uppercase fw-medium text-truncate mb-0">
                                    BASIC SALARY
                                </p>
                            </div>
                        </div>
                        <div class="d-flex align-items-end justify-content-between mt-4">
                            <div>
                                <h4 class="fs-22 fw-semibold ff-secondary mb-4">
                                   ₦ {{ $filters.number_format(dashboardData.payroll_data.basic_salary, 2) }}
                                </h4>
                                <router-link to="/profile?tab=4" class="small text-decoration-underline">View more</router-link>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-soft-dark rounded fs-3">
                                    <i class="mdi mdi-cash-multiple text-dark"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-4">
                <div class="card h-100 mb-0 card-animate overflow-hidden" style="min-height: 100px;">
                    <common-skeleton-loader position="absolute" v-if="isLoading" />
                    <div v-else class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1 overflow-hidden">
                                <p class="text-uppercase fw-medium text-truncate mb-0">
                                    NET PAY
                                </p>
                            </div>
                        </div>
                        <div class="d-flex align-items-end justify-content-between mt-4">
                            <div>
                                <h4 class="fs-22 fw-semibold ff-secondary mb-4">
                                    ₦ {{ $filters.number_format(dashboardData.payroll_data.net_pay, 2) }}
                                </h4>
                                <router-link to="/profile?tab=4" class="small text-decoration-underline">View more</router-link>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-soft-info rounded fs-3">
                                    <i class="mdi mdi-wallet-outline text-info"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-4">
                <div class="card h-100 mb-0 card-animate overflow-hidden" style="min-height: 100px;">
                    <common-skeleton-loader position="absolute" v-if="isLoading" />
                    <div v-else class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1 overflow-hidden">
                                <p class="text-uppercase fw-medium text-truncate mb-0">
                                    GROSS PAY
                                </p>
                            </div>
                        </div>
                        <div class="d-flex align-items-end justify-content-between mt-4">
                            <div>
                                <h5 class="fs-22 fw-semibold ff-secondary mb-4">
                                    ₦ {{ $filters.number_format(dashboardData.payroll_data.gross_pay, 2) }}
                                </h5>
                                <router-link to="/profile?tab=4" class="small text-decoration-underline">View more</router-link>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-soft-warning rounded fs-3">
                                    <i class="mdi mdi-chart-line text-warning"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row gy-3">
            <div class="col-md-5 order-md-2">
                <div class="card mb-3 card-animate" style="min-height: 100px;">
                    <common-skeleton-loader v-if="isLoading" />
                    <div v-else class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1 overflow-hidden">
                                <small class="text-muted text-truncate small">Your Employee Number</small>
                                <p v-if="dashboardData.employee" class="mt-2 text-uppercase fw-medium text-truncate mb-0">
                                    {{ dashboardData.employee.employee_number }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card mb-0 card-animate" style="min-height: 100px;">
                    <common-skeleton-loader v-if="isLoading" />
                    <div v-else class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1 overflow-hidden">
                                <small class="text-muted text-truncate small">Your Active Account </small>
                                <p v-if="dashboardData.employee" class="mt-2 text-uppercase fw-medium text-truncate mb-0">
                                    <span v-if="dashboardData.employee.bank_account"> 
                                        {{ dashboardData.employee.bank_account.account_number }}<br />
                                        <span v-if="dashboardData.employee.bank_account.bank">{{ dashboardData.employee.bank_account.bank.name }}</span>
                                        <span v-else>{{ dashboardData.employee.bank_account.given_bank_name }}</span>
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-7">
                <div class="card h-100 mb-0 overflow-hidden" style="min-height: 50px;">
                    <common-skeleton-loader v-if="isLoading" />
                    <div v-else class="card-body bg-marketplace d-flex"
                        :style="`background-image: url(${absoluteUrl('/images/bg/marketplace.png')}); background-size: cover;`">
                        <div class="flex-grow-1">
                            <div class="w-100">
                                <h5 class="card-title mb-4">Your current place of employment</h5>
                                <div v-if="dashboardData.employee" class="">
                                    <span v-if="dashboardData.employee.civil_service">{{  dashboardData.employee.civil_service.title }}</span>
                                    <p>
                                        <strong class="text-uppercase fs-5"> 
                                            <span v-if="dashboardData.employee.department">{{  dashboardData.employee.department.name }}, </span>
                                            <span v-if="dashboardData.employee.ministry">{{  dashboardData.employee.ministry.name }}</span>
                                        </strong>
                                    </p>
                                </div>
                                <hr />
                                <small class="text-muted text-truncate small">Your Salary Structure</small>
                                <p v-if="dashboardData.employee" class="mt-2 text-uppercase fw-medium text-truncate mb-0">
                                    <span v-if="dashboardData.employee.salary_structure"> {{ dashboardData.employee.salary_structure.title }} </span> &nbsp;
                                    <span v-if="dashboardData.employee.grade_level">{{ dashboardData.employee.grade_level.title }} / </span>
                                    <span v-if="dashboardData.employee.step_level">{{ dashboardData.employee.step_level.title }}</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    name: 'dashboard',
    data() {
        return {
            isLoading: false,
            forceUpdateProfile: false
        }
    },
    computed: {
        activeUser() {
            return this.$store.state.AppActiveUser
        },
        dashboardData() {
            return this.$store.state.dashboardData
        }
    },
    methods: {
        fetchDashboardData() {
            this.isLoading = true;
            this.$store.dispatch('fetchDashboardData')
                .then((response) => {
                    this.isLoading = false;
                    if (response.data.success) {
                        // if (!response.data.data.user?.image) {
                        //     this.forceUpdateProfile = true
                        // }
                    }
                })
        },
        closeForceUpdate() {
            this.fetchDashboardData()
            this.initializeAll()
        },
        initializeAll() {
            this.forceUpdateProfile = false;
        },
    },
    created() {
        this.fetchDashboardData()
    }
}
</script>
